import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import useSEO from '@/hooks/useSEO';
import { ChevronRight, Sparkles, Trophy, Lock, Shuffle, BookOpen, Globe2 } from 'lucide-react';

/**
 * /countries/:slug — Country hub.
 *
 * Free tier: facts panel + "Try one free quiz today" CTA (locked once used).
 * Paid tier: facts + unlimited quiz CTA.
 */
export default function CountryHub() {
    const { slug } = useParams();
    const { user } = useAuth();
    const [hub, setHub] = useState(null);
    const [facts, setFacts] = useState([]);
    const [factsLoading, setFactsLoading] = useState(false);
    const [err, setErr] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get(`/countries/${slug}`);
            setHub(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Failed to load');
        }
    }, [slug]);

    useEffect(() => { refresh(); }, [refresh]);

    const showFacts = async () => {
        setFactsLoading(true);
        try {
            const { data } = await api.get(`/countries/${slug}/facts?limit=5`);
            setFacts(data.facts || []);
        } finally { setFactsLoading(false); }
    };

    useSEO({
        title: hub?.country?.name ? `${hub.country.name} facts & quiz | Linguistic Quest` : 'Country | Linguistic Quest',
        description: hub?.country?.blurb || 'Random facts and a daily quiz about countries from around the world.',
        path: `/countries/${slug}`,
    });

    if (err) return <main className="max-w-3xl mx-auto px-6 py-16">Error: {err}</main>;
    if (!hub) return <main className="max-w-3xl mx-auto px-6 py-16 text-lq-ink-500">Loading…</main>;

    const c = hub.country;
    const quizLive = (hub.quiz_status || 'coming_soon') === 'live';
    const hasQuiz = quizLive && (hub.quiz_question_count || 0) > 0;
    const quizLocked = !hub.is_paid && hub.daily_free_used;
    const quizComingSoon = !quizLive;
    const noQuestionsYet = quizLive && (hub.quiz_question_count || 0) === 0;

    return (
        <main className="max-w-5xl mx-auto px-6 py-12" data-testid="page-country-hub">
            <nav className="text-sm text-lq-ink-500 flex items-center gap-1" aria-label="Breadcrumb">
                <Link to="/countries" className="hover:text-lq-purple-700">Countries</Link>
                <ChevronRight size={14}/> <span className="text-lq-ink-900">{c.name}</span>
            </nav>

            <div className="mt-4 flex flex-wrap items-center gap-4">
                <span className="text-7xl" aria-hidden="true">{c.flag_emoji}</span>
                <div>
                    <h1 className="font-display text-4xl md:text-5xl font-semibold text-lq-ink-black">{c.name}</h1>
                    {c.region && <p className="mt-1 text-lq-ink-500 inline-flex items-center gap-1"><Globe2 size={14}/> {c.region}</p>}
                </div>
            </div>

            {c.blurb && <p className="mt-6 text-lg text-lq-ink-700 leading-relaxed max-w-2xl">{c.blurb}</p>}

            {/* Linked languages */}
            {(c.language_slugs || []).length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2" data-testid="country-languages">
                    <span className="text-xs uppercase tracking-wide text-lq-ink-500 mr-1 self-center">Languages here:</span>
                    {c.language_slugs.map((slug) => (
                        <Link key={slug} to={`/languages/${slug}`} className="px-3 py-1 rounded-full bg-white border border-lq-ink-100 text-sm text-lq-ink-700 hover:border-lq-purple-300 hover:text-lq-purple-700 capitalize transition-colors">
                            {slug}
                        </Link>
                    ))}
                </div>
            )}

            <div className="mt-10 grid md:grid-cols-2 gap-6">
                {/* FACTS PANEL */}
                <section className="bg-white rounded-2xl border border-lq-ink-100 p-6" data-testid="country-facts-panel">
                    <h2 className="font-display text-xl font-semibold text-lq-ink-900 inline-flex items-center gap-2"><Sparkles size={18} className="text-lq-peach-500"/> Random facts</h2>
                    <p className="mt-1 text-sm text-lq-ink-500">Free for everyone. Tap the button to shuffle a new set.</p>
                    {(facts.length === 0 ? hub.sample_facts : facts).length > 0 ? (
                        <ul className="mt-4 space-y-3" data-testid="country-facts-list">
                            {(facts.length > 0 ? facts : hub.sample_facts).map((f) => (
                                <li key={f.id} className="text-sm text-lq-ink-900 leading-relaxed">
                                    <span className="text-lq-peach-500 mr-2">●</span>{f.fact}
                                    {f.category && <span className="ml-2 text-xs text-lq-ink-500">· {f.category}</span>}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="mt-4 text-sm text-lq-ink-500">No facts uploaded for {c.name} yet — check back soon.</p>
                    )}
                    {(hub.sample_facts.length > 0 || facts.length > 0) && (
                        <button
                            onClick={showFacts}
                            disabled={factsLoading}
                            className="mt-5 inline-flex items-center gap-1 px-5 py-2 rounded-full bg-lq-peach-500 text-white font-semibold hover:opacity-90 disabled:opacity-50"
                            data-testid="country-facts-shuffle"
                        >
                            <Shuffle size={14}/> {factsLoading ? 'Shuffling…' : 'Show me more'}
                        </button>
                    )}
                </section>

                {/* QUIZ PANEL */}
                <section className="bg-white rounded-2xl border border-lq-ink-100 p-6 flex flex-col" data-testid="country-quiz-panel">
                    <h2 className="font-display text-xl font-semibold text-lq-ink-900 inline-flex items-center gap-2"><Trophy size={18} className="text-lq-purple-500"/> Country quiz</h2>
                    <p className="mt-1 text-sm text-lq-ink-500">
                        {hub.is_paid
                            ? 'Unlimited play. Each correct answer adds 2 points to your family leaderboard.'
                            : 'One free question per day. Subscribe for unlimited play and to keep all your points.'}
                    </p>
                    <p className="mt-2 text-xs text-lq-ink-500">{hub.quiz_question_count || 0} question{hub.quiz_question_count === 1 ? '' : 's'} available · 2 pts per correct answer</p>

                    <div className="mt-auto pt-5">
                        {quizComingSoon ? (
                            <div className="px-4 py-3 rounded-xl bg-lq-peach-100 border border-lq-peach-300 text-sm text-lq-ink-900 inline-flex items-center gap-2" data-testid="country-quiz-coming-soon">
                                <Sparkles size={14} className="text-lq-peach-600"/> Quiz coming soon for {c.name} — our editors are preparing the questions.
                            </div>
                        ) : noQuestionsYet ? (
                            <div className="px-4 py-3 rounded-xl bg-lq-ink-100 text-sm text-lq-ink-500 text-center" data-testid="country-quiz-empty">
                                No quiz questions yet for {c.name}. Our editors are working on it.
                            </div>
                        ) : !user ? (
                            <Link to={`/login?next=/countries/${slug}`} className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700" data-testid="country-quiz-login">
                                Sign in to play
                            </Link>
                        ) : quizLocked ? (
                            <div data-testid="country-quiz-locked">
                                <div className="px-4 py-3 rounded-xl bg-lq-warn/10 border border-lq-warn/30 text-sm text-lq-ink-900 inline-flex items-center gap-2"><Lock size={14}/> You've used today's free quiz. Comes back tomorrow.</div>
                                <Link to="/pricing" className="mt-3 inline-flex items-center gap-1 px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700" data-testid="country-quiz-upgrade">
                                    Subscribe for unlimited →
                                </Link>
                            </div>
                        ) : (
                            <Link
                                to={`/countries/${slug}/quiz`}
                                className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700"
                                data-testid="country-quiz-start"
                            >
                                <Trophy size={14}/> {hub.is_paid ? 'Play quiz' : 'Take today\'s free question'}
                            </Link>
                        )}
                    </div>
                </section>
            </div>

            <section className="mt-10 bg-lq-purple-50 border border-lq-purple-100 rounded-2xl p-5">
                <h2 className="font-display text-lg font-semibold text-lq-ink-900 inline-flex items-center gap-2"><BookOpen size={16}/> Learn the language too</h2>
                <p className="mt-1 text-sm text-lq-ink-700">Quizzes go deeper when you can also greet someone in the country's language. Your family already learns one language to unlock related country quizzes.</p>
            </section>
        </main>
    );
}
